import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TriageSubmission } from "@/types/database";
import { format } from "date-fns";
import { AlertTriangle, Clock, User, Activity, Eye } from "lucide-react";
import { TriageDetailsModal } from "./TriageDetailsModal";
import { useAuth } from "@/hooks/useAuth";

export function TriageManagement() {
  const { profile } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'submitted' | 'reviewed'>('all');
  const [selectedTriageId, setSelectedTriageId] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const openDetailsModal = (triageId: string) => {
    setSelectedTriageId(triageId);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    // We don't reset selectedTriageId immediately to avoid UI flicker while modal closes
    setTimeout(() => setSelectedTriageId(null), 300);
  };

  const { data: triageSubmissions, isLoading } = useQuery({
    queryKey: ['triage-submissions', profile?.clinic_id, selectedStatus],
    queryFn: async () => {
      if (!profile?.clinic_id) return [];

      try {
        // Use a two-step query approach to avoid Supabase schema cache issues
        // Step 1: Get triage submissions without joins
        let query = supabase
          .from('triage_submissions')
          .select('*')
          .eq('clinic_id', profile.clinic_id)
          .order('created_at', { ascending: false });

        if (selectedStatus !== 'all') {
          query = query.eq('status', selectedStatus);
        }

        const { data: triageData, error: triageError } = await query;

        if (triageError) {
          console.error('Error fetching triage submissions:', triageError);
          return [];
        }

        if (!triageData || triageData.length === 0) {
          return [];
        }

        // Step 2: Get patient profiles using the same proven RPC approach as Patient Management
        const patientIds = triageData.map(submission => submission.patient_id);
        const reviewerIds = triageData.filter(submission => submission.reviewed_by)
          .map(submission => submission.reviewed_by);
        
        // Create a map of profiles for easy lookup
        const profilesMap = {};
        
        if (patientIds.length > 0) {
          console.log('DEBUG: Using get_patients_by_clinic RPC for patient data');
          
          // Use the same RPC function that works in Patient Management
          const { data: allPatientsData, error: rpcError } = await supabase
            .rpc('get_patients_by_clinic', { search_query: '' });
          
          if (allPatientsData && !rpcError) {
            console.log('DEBUG: RPC returned', allPatientsData.length, 'patients');
            // Filter to only the patients we need and map to profiles format
            allPatientsData
              .filter(patient => patientIds.includes(patient.id))
              .forEach(patient => {
                profilesMap[patient.id] = {
                  id: patient.id,
                  first_name: patient.first_name || '',
                  last_name: patient.last_name || '',
                  email: patient.email || ''
                };
              });
              
            console.log('DEBUG: Mapped', Object.keys(profilesMap).length, 'patients from RPC');
          } else {
            console.error('DEBUG: RPC failed:', rpcError?.message);
          }
        }
        
        // Handle reviewer profiles (staff/doctors) - these might need direct query since they won't be in patient RPC
        const missingReviewerIds = reviewerIds.filter(id => id && !profilesMap[id]);
        if (missingReviewerIds.length > 0) {
          console.log('DEBUG: Fetching reviewer profiles for:', missingReviewerIds);
          const { data: reviewerProfiles, error: reviewerError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email')
            .in('id', missingReviewerIds);
            
          if (reviewerProfiles && !reviewerError) {
            reviewerProfiles.forEach(profile => {
              profilesMap[profile.id] = profile;
            });
          }
        }
        
        // Fallback: For any remaining missing patients, try direct profiles query
        const missingPatientIds = patientIds.filter(id => !profilesMap[id]);
        if (missingPatientIds.length > 0) {
          console.log('DEBUG: Some patients missing from RPC, trying direct query for:', missingPatientIds);
          
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email')
            .in('id', missingPatientIds);
          
          if (profilesData && !profilesError) {
            profilesData.forEach(profile => {
              profilesMap[profile.id] = profile;
            });
          }
        }

        // Combine the data manually with enhanced patient info extraction
        const combinedData = triageData.map(submission => {
          // If patient profile exists in profile map, use it
          if (profilesMap[submission.patient_id]) {
            return {
              ...submission,
              profiles: profilesMap[submission.patient_id],
              reviewer: submission.reviewed_by ? profilesMap[submission.reviewed_by] : undefined
            };
          }
          
          // Extract patient info from email-formatted patient_id if possible
          if (submission.patient_id && submission.patient_id.includes('@')) {
            const emailParts = submission.patient_id.split('@')[0].split('.');
            const firstName = emailParts[0] ? emailParts[0].charAt(0).toUpperCase() + emailParts[0].slice(1) : 'Unknown';
            const lastName = emailParts[1] ? emailParts[1].charAt(0).toUpperCase() + emailParts[1].slice(1) : 'Patient';
            
            return {
              ...submission,
              profiles: {
                first_name: firstName,
                last_name: lastName,
                email: submission.patient_id
              },
              reviewer: submission.reviewed_by ? profilesMap[submission.reviewed_by] : undefined
            };
          }
          
          // Fallback to standard unknown patient formatting
          return {
            ...submission,
            profiles: { 
              first_name: 'Unknown',
              last_name: 'Patient',
              email: 'No email'
            },
            reviewer: submission.reviewed_by ? profilesMap[submission.reviewed_by] : undefined
          };
        });

        return combinedData as (TriageSubmission & {
          profiles: { first_name: string; last_name: string; email: string },
          reviewer?: { first_name: string; last_name: string }
        })[];
      } catch (err) {
        console.error('Exception in triage submissions query:', err);
        return [];
      }
    },
    enabled: !!profile?.clinic_id
  });

  const getUrgencyColor = (urgency: TriageSubmission['urgency']) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500 text-white';
      case 'urgent': return 'bg-orange-500 text-white';
      case 'standard': return 'bg-yellow-500 text-white';
      case 'non-urgent': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status: TriageSubmission['status']) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'reviewed': return 'bg-green-100 text-green-800';
      case 'processed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Triage Management
            </CardTitle>
            <CardDescription>Review and manage patient triage submissions</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={selectedStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('all')}
            >
              All
            </Button>
            <Button 
              variant={selectedStatus === 'submitted' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('submitted')}
            >
              New
            </Button>
            <Button 
              variant={selectedStatus === 'reviewed' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('reviewed')}
            >
              Reviewed
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">Loading triage submissions...</div>
        ) : !triageSubmissions?.length ? (
          <div className="text-center py-4 text-gray-500">
            No triage submissions found
          </div>
        ) : (
          <div className="space-y-4">
            {triageSubmissions.map((submission) => (
              <div 
                key={submission.id} 
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-4">
                    <Badge className={getUrgencyColor(submission.urgency)}>
                      {submission.urgency.toUpperCase()}
                    </Badge>
                    <Badge className={getStatusColor(submission.status)}>
                      {submission.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    {format(new Date(submission.created_at), 'MMM dd, yyyy h:mm a')}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="font-medium">
                        {submission.profiles.first_name} {submission.profiles.last_name}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({submission.profiles.email})
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {submission.symptoms.slice(0, 3).join(', ')}
                      {submission.symptoms.length > 3 && '...'}
                    </div>
                  </div>

                  <div>
                    {submission.red_flags.length > 0 && (
                      <div className="flex items-start gap-2 text-red-600 text-sm">
                        <AlertTriangle className="w-4 h-4 mt-0.5" />
                        <div>
                          {submission.red_flags.slice(0, 2).join(', ')}
                          {submission.red_flags.length > 2 && '...'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openDetailsModal(submission.id)}
                    className="flex items-center gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </Button>
                  {submission.status === 'submitted' && (
                    <Button size="sm">
                      Review Now
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Triage Details Modal */}
    <TriageDetailsModal
      isOpen={isDetailsModalOpen}
      onClose={closeDetailsModal}
      triageId={selectedTriageId}
    />
    </>
  );
} 